/**
 * WebSocket service for real-time BOB analysis collaboration using Socket.IO
 *
 * Design principles (from realtime-collaboration-design.md):
 * 1. Optimistic Updates: Broadcast changes IMMEDIATELY, save to DB in background
 * 2. Field-Level Locking: When User A focuses a field, it becomes readonly for User B
 * 3. Self-Update Filtering: Clients skip their own updates to prevent cursor jumping
 * 4. No React Query Refetch: WebSocket is the single source of truth for real-time updates
 */

import { io, Socket } from 'socket.io-client'
import { useAuthStore } from '@/lib/stores/authStore'

// ============================================================================
// Types
// ============================================================================

export interface BobContentUpdate {
  bob_id: string
  user_id: string
  section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming'
  category: string
  values: string[]
  timestamp: number
}

interface UserInfo {
  name: string
  email: string
  avatar?: string
}

interface ActiveUser extends UserInfo {
  sid: string
}

interface EditingSectionEvent {
  user_id: string
  user_name: string
  section_key: string
  bob_id: string
}

// Event handler types
type BobContentUpdateHandler = (update: BobContentUpdate) => void
type UserJoinedHandler = (userId: string, userInfo: ActiveUser) => void
type UserLeftHandler = (userId: string) => void
type UserEditingSectionHandler = (
  userId: string,
  userName: string,
  sectionKey: string
) => void
type UserStoppedEditingHandler = (userId: string) => void
type ActiveUsersHandler = (users: Record<string, ActiveUser>) => void
type ConnectionStatusHandler = (connected: boolean) => void

// ============================================================================
// WebSocket Service (Singleton)
// ============================================================================

class WebSocketService {
  private socket: Socket | null = null
  private isConnected: boolean = false
  private currentBobId: string | null = null
  private reconnectAttempts: number = 0
  private readonly maxReconnectAttempts: number = 10

  // Event handlers stored as Sets for multiple subscribers
  private contentUpdateHandlers = new Set<BobContentUpdateHandler>()
  private userJoinedHandlers = new Set<UserJoinedHandler>()
  private userLeftHandlers = new Set<UserLeftHandler>()
  private userEditingSectionHandlers = new Set<UserEditingSectionHandler>()
  private userStoppedEditingHandlers = new Set<UserStoppedEditingHandler>()
  private activeUsersHandlers = new Set<ActiveUsersHandler>()
  private connectionStatusHandlers = new Set<ConnectionStatusHandler>()

  // ============================================================================
  // Connection Management
  // ============================================================================

  /**
   * Initialize WebSocket connection.
   * Returns a promise that resolves when connected.
   */
  connect(): Promise<void> {
    // If already connected, resolve immediately
    if (this.socket?.connected) {
      return Promise.resolve()
    }

    // If socket exists but not connected, wait for connection
    if (this.socket) {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('WebSocket connection timeout'))
        }, 10000)

        if (this.socket?.connected) {
          clearTimeout(timeout)
          resolve()
        } else {
          this.socket?.once('connect', () => {
            clearTimeout(timeout)
            resolve()
          })
          this.socket?.once('connect_error', err => {
            clearTimeout(timeout)
            reject(err)
          })
        }
      })
    }

    // Runtime configuration injected by entrypoint.sh at container startup
    interface RuntimeConfig {
      API_BASE_URL?: string
    }

    // Robust resolver for API base URL. Accepts:
    // - absolute URLs (https://...)
    // - protocol-relative (//example.com)
    // - host-only (example.com or example.com:5000)
    // - relative paths (/api)
    // Falls back to window.location.origin when nothing is provided.
    function resolveApiBaseUrl(runtime?: string, env?: string): string {
      const isSentinel = (v?: string) => !v || v === '__API_BASE_URL__'

      const candidates = [runtime, env]
      for (const raw of candidates) {
        if (isSentinel(raw)) continue
        const v = (raw ?? '').trim().replace(/^['"]|['"]$/g, '')
        if (!v) continue

        // Absolute URL
        try {
          const u = new URL(v)
          return (u.origin + (u.pathname === '/' ? '' : u.pathname)).replace(/\/+$/g, '')
        } catch {
          // not absolute
        }

        // Protocol-relative
        if (v.startsWith('//')) {
          return `${window.location.protocol}${v}`.replace(/\/+$/g, '')
        }

        // Relative path
        if (v.startsWith('/')) {
          return (window.location.origin + v).replace(/\/+$/g, '')
        }

        // Host-only -> assume same protocol as current page
        try {
          return `${window.location.protocol}//${v}`.replace(/\/+$/g, '')
        } catch {
          continue
        }
      }

      return window.location.origin
    }

    const runtimeConfig = (window as Window & { __RUNTIME_CONFIG__?: RuntimeConfig }).__RUNTIME_CONFIG__
    const envUrl = import.meta.env.VITE_API_BASE_URL || ''
    const baseUrl = resolveApiBaseUrl(runtimeConfig?.API_BASE_URL, envUrl)

    console.info('[WebSocket] Resolved API base URL:', baseUrl)

    this.socket = io(baseUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    })

    this.setupEventListeners()

    // Return promise that resolves when connected
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('WebSocket connection timeout'))
      }, 10000)

      this.socket?.once('connect', () => {
        clearTimeout(timeout)
        resolve()
      })
      this.socket?.once('connect_error', err => {
        clearTimeout(timeout)
        reject(err)
      })
    })
  }

  /**
   * Setup Socket.IO event listeners
   */
  private setupEventListeners() {
    if (!this.socket) return

    this.socket.on('connect', () => {
      this.isConnected = true
      this.reconnectAttempts = 0
      this.notifyConnectionStatus(true)

      // Re-join current BOB analysis if we were in one (reconnection scenario)
      if (this.currentBobId) {
        this.joinBobAnalysis(this.currentBobId)
      }
    })

    this.socket.on('disconnect', reason => {
      this.isConnected = false
      this.notifyConnectionStatus(false)

      if (reason === 'io server disconnect') {
        // Server disconnected us, manually reconnect
        this.socket?.connect()
      }
    })

    this.socket.on('connect_error', error => {
      console.error('[WebSocket] Connection error:', error.message)
      this.reconnectAttempts++
      this.notifyConnectionStatus(false)
    })

    // BOB content updates from other users
    this.socket.on('bob_content_updated', (data: BobContentUpdate) => {
      // Note: Backend already filters out sender via skip_sid
      // This is a safety check - skip if update is from current user
      const currentUserId = useAuthStore.getState().user?.id
      if (data.user_id === currentUserId) {
        return
      }

      this.contentUpdateHandlers.forEach(handler => handler(data))
    })

    // Category added by other users
    this.socket.on('bob_category_added', (data: {
      bob_id: string
      user_id: string
      section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming'
      category: string
      timestamp: number
    }) => {
      const currentUserId = useAuthStore.getState().user?.id
      if (data.user_id === currentUserId) {
        return // Skip self-updates
      }

      // Notify parent component to refetch BOB analysis
      this.contentUpdateHandlers.forEach(handler =>
        handler({
          ...data,
          values: [], // New category starts with empty values
        })
      )
    })

    // Category deleted by other users
    this.socket.on('bob_category_deleted', (data: {
      bob_id: string
      user_id: string
      section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming'
      category: string
      timestamp: number
    }) => {
      const currentUserId = useAuthStore.getState().user?.id
      if (data.user_id === currentUserId) {
        return // Skip self-updates
      }

      // Trigger a refetch by notifying handlers
      // We use a special marker to indicate deletion
      this.contentUpdateHandlers.forEach(handler =>
        handler({
          ...data,
          values: [], // Empty to trigger refetch
        })
      )
    })

    // User presence events
    this.socket.on(
      'active_users',
      (data: { users: Record<string, ActiveUser>; bob_id: string }) => {
        this.activeUsersHandlers.forEach(handler => handler(data.users))
      }
    )

    this.socket.on(
      'user_joined',
      (data: { user_id: string; user_info: ActiveUser; bob_id: string }) => {
        this.userJoinedHandlers.forEach(handler =>
          handler(data.user_id, data.user_info)
        )
      }
    )

    this.socket.on('user_left', (data: { user_id: string; bob_id: string }) => {
      this.userLeftHandlers.forEach(handler => handler(data.user_id))
    })

    // Field-level locking events
    this.socket.on('user_editing_section', (data: EditingSectionEvent) => {
      // Notify all handlers including if it's the current user (for confirmation)
      this.userEditingSectionHandlers.forEach(handler =>
        handler(data.user_id, data.user_name, data.section_key)
      )
    })

    this.socket.on(
      'user_stopped_editing',
      (data: { user_id: string; bob_id: string }) => {
        this.userStoppedEditingHandlers.forEach(handler =>
          handler(data.user_id)
        )
      }
    )

    this.socket.on('error', (error: { message: string }) => {
      console.error('[WebSocket] Server error:', error.message)
    })
  }

  private notifyConnectionStatus(connected: boolean) {
    this.connectionStatusHandlers.forEach(handler => handler(connected))
  }

  // ============================================================================
  // Room Management
  // ============================================================================

  /**
   * Join a BOB analysis room for collaboration.
   * Should be called AFTER initial data is loaded from API.
   */
  joinBobAnalysis(bobId: string) {
    if (!this.socket?.connected) {
      console.warn('[WebSocket] Not connected, cannot join BOB analysis')
      return
    }

    const user = useAuthStore.getState().user
    if (!user) {
      console.error('[WebSocket] No user found, cannot join BOB analysis')
      return
    }

    // Leave previous room if different
    if (this.currentBobId && this.currentBobId !== bobId) {
      this.leaveBobAnalysis()
    }

    this.currentBobId = bobId

    // Build a proper display name: prefer displayName, else firstName + lastName
    const displayName = user.displayName
      || (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : null)
      || user.email

    this.socket.emit('join_bob_analysis', {
      bob_id: bobId,
      user_id: user.id,
      user_info: {
        name: displayName,
        email: user.email,
      },
    })
  }

  /**
   * Leave current BOB analysis room
   */
  leaveBobAnalysis() {
    if (!this.socket?.connected || !this.currentBobId) return

    const user = useAuthStore.getState().user
    if (!user) return

    this.socket.emit('leave_bob_analysis', {
      bob_id: this.currentBobId,
      user_id: user.id,
    })

    this.currentBobId = null
  }

  // ============================================================================
  // Field-Level Locking
  // ============================================================================

  /**
   * Notify that user started editing a section (acquires lock).
   * Call this on onFocus of an editable field.
   *
   * @param sectionKey - Format: "section.category" e.g., "beeldvorming.oorzaak"
   */
  startEditingSection(sectionKey: string) {
    if (!this.socket?.connected || !this.currentBobId) return

    const user = useAuthStore.getState().user
    if (!user) return

    this.socket.emit('start_editing_section', {
      bob_id: this.currentBobId,
      user_id: user.id,
      section_key: sectionKey,
    })
  }

  /**
   * Notify that user stopped editing (releases lock).
   * Call this on onBlur of an editable field.
   */
  stopEditingSection() {
    if (!this.socket?.connected || !this.currentBobId) return

    const user = useAuthStore.getState().user
    if (!user) return

    this.socket.emit('stop_editing_section', {
      bob_id: this.currentBobId,
      user_id: user.id,
    })
  }

  // ============================================================================
  // Content Broadcasting
  // ============================================================================

  /**
   * Broadcast content update to other users IMMEDIATELY.
   * This is the optimistic update pattern - broadcast first, save to DB later.
   *
   * Call this on every content change (with minimal debounce for rapid typing).
   * The save mutation should happen separately in the background.
   */
  broadcastContentUpdate(
    section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming',
    category: string,
    values: string[]
  ) {
    if (!this.socket?.connected || !this.currentBobId) return

    const user = useAuthStore.getState().user
    if (!user) return

    const update: BobContentUpdate = {
      bob_id: this.currentBobId,
      user_id: user.id,
      section,
      category,
      values,
      timestamp: Date.now(),
    }

    this.socket.emit('bob_content_update', update)
  }

  /**
   * Broadcast that a new category was added to a section.
   * This notifies other users to refetch the BOB analysis to see the new category.
   *
   * @param section - The section where the category was added
   * @param categoryName - The name of the new category
   */
  broadcastCategoryAdded(
    section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming',
    categoryName: string
  ) {
    if (!this.socket?.connected || !this.currentBobId) return

    const user = useAuthStore.getState().user
    if (!user) return

    this.socket.emit('bob_category_added', {
      bob_id: this.currentBobId,
      user_id: user.id,
      section,
      category: categoryName,
      timestamp: Date.now(),
    })
  }

  /**
   * Broadcast that a category was deleted from a section.
   * This notifies other users to refetch the BOB analysis to remove the category.
   *
   * @param section - The section where the category was deleted
   * @param categoryName - The name of the deleted category
   */
  broadcastCategoryDeleted(
    section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming',
    categoryName: string
  ) {
    if (!this.socket?.connected || !this.currentBobId) return

    const user = useAuthStore.getState().user
    if (!user) return

    this.socket.emit('bob_category_deleted', {
      bob_id: this.currentBobId,
      user_id: user.id,
      section,
      category: categoryName,
      timestamp: Date.now(),
    })
  }

  // ============================================================================
  // Event Subscription
  // ============================================================================

  /**
   * Subscribe to content updates from other users.
   * Returns unsubscribe function.
   */
  onContentUpdate(handler: BobContentUpdateHandler): () => void {
    this.contentUpdateHandlers.add(handler)
    return () => this.contentUpdateHandlers.delete(handler)
  }

  /**
   * Subscribe to user joined events.
   */
  onUserJoined(handler: UserJoinedHandler): () => void {
    this.userJoinedHandlers.add(handler)
    return () => this.userJoinedHandlers.delete(handler)
  }

  /**
   * Subscribe to user left events.
   */
  onUserLeft(handler: UserLeftHandler): () => void {
    this.userLeftHandlers.add(handler)
    return () => this.userLeftHandlers.delete(handler)
  }

  /**
   * Subscribe to user editing section events (field locking).
   * Handler receives: userId, userName, sectionKey
   */
  onUserEditingSection(handler: UserEditingSectionHandler): () => void {
    this.userEditingSectionHandlers.add(handler)
    return () => this.userEditingSectionHandlers.delete(handler)
  }

  /**
   * Subscribe to user stopped editing events (lock release).
   */
  onUserStoppedEditing(handler: UserStoppedEditingHandler): () => void {
    this.userStoppedEditingHandlers.add(handler)
    return () => this.userStoppedEditingHandlers.delete(handler)
  }

  /**
   * Subscribe to active users updates.
   */
  onActiveUsers(handler: ActiveUsersHandler): () => void {
    this.activeUsersHandlers.add(handler)
    return () => this.activeUsersHandlers.delete(handler)
  }

  /**
   * Subscribe to connection status changes.
   */
  onConnectionStatus(handler: ConnectionStatusHandler): () => void {
    this.connectionStatusHandlers.add(handler)
    // Immediately notify of current status
    handler(this.isConnected)
    return () => this.connectionStatusHandlers.delete(handler)
  }

  // ============================================================================
  // Lifecycle
  // ============================================================================

  /**
   * Disconnect from WebSocket
   */
  disconnect() {
    if (this.currentBobId) {
      this.leaveBobAnalysis()
    }

    this.socket?.disconnect()
    this.socket = null
    this.isConnected = false
  }

  /**
   * Check if connected
   */
  get connected(): boolean {
    return this.isConnected && this.socket?.connected === true
  }

  /**
   * Get current BOB ID
   */
  get currentBob(): string | null {
    return this.currentBobId
  }
}

// Export singleton instance
export const websocketService = new WebSocketService()
